<script setup>
import { ref, watch } from "vue";
import { useModalogStore } from "@/stores/modalog.js";

/**
 * "Move…" trigger + dropdown shared by GiftCard and the item detail page so
 * the menu looks and behaves identically everywhere. The trigger's look is
 * caller-supplied via triggerClass; the panel is fixed.
 *
 * The open panel anchors around the trigger (per Figma 224-2196 the dropdown
 * reads as a hover state growing out of the action): the panel offsets up and
 * toward the anchored edge, and an offset-compensated invisible twin of the
 * trigger reserves its exact footprint as the panel's first row — so the
 * trigger itself never moves, it just goes white on the navy panel. The
 * label's open color is inline style so no caller hover rule can override it.
 */
const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
    list: {
        type: Object,
        required: true,
    },
    // Which trigger edge the panel pins to (it grows toward the other side).
    align: {
        type: String,
        default: "left",
        validator: (value) => ["left", "right", "center"].includes(value),
    },
    triggerClass: {
        type: String,
        default:
            "inline-flex items-center gap-1.5 rounded-[4px] px-1.5 py-1.5 font-brand text-[13px] font-semibold text-[#565a77] transition hover:bg-brand-cream hover:text-brand-navy",
    },
    iconClass: {
        type: String,
        default: "h-[11px] w-[14px]",
    },
    // A parsing item can't be moved yet; the trigger renders disabled.
    disabled: {
        type: Boolean,
        default: false,
    },
});

const { showModalog } = useModalogStore();

const open = ref(false);

// Parsing can start under an open menu (the poll swaps the item in place).
watch(
    () => props.disabled,
    (disabled) => {
        if (disabled) {
            open.value = false;
        }
    },
);

function close() {
    open.value = false;
}

function moveToList() {
    close();
    showModalog({
        type: "ModalogMoveCopyItemToWishList",
        props: {
            isOwner: true,
            item: props.item,
            listUuid: props.list.uuid,
        },
        isConfirmDisabled: true,
    });
}

function moveToNewList() {
    close();
    showModalog({
        type: "ModalogMoveCopyItemToNewWishList",
        props: {
            isOwner: true,
            item: props.item,
        },
        isConfirmDisabled: true,
    });
}

// The panel shifts 8px toward the anchored edge (and 6px up) so the menu
// rows' 14px text indent lines up with the trigger's icon; the twin wrapper
// pads the same amounts back so the reserved footprint stays put.
const alignClasses = {
    left: "-left-2",
    right: "-right-2",
    center: "left-1/2 -translate-x-1/2",
};

const twinPadClasses = {
    left: "pl-2 pt-1.5",
    right: "pr-2 pt-1.5 text-right",
    center: "pt-1.5",
};

// border-0 + m-0 + bg-transparent guard against legacy button styling
// boxing each row; only the top divider remains.
const menuItemClasses =
    "m-0 block w-full appearance-none whitespace-nowrap border-0 border-t border-solid border-white/10 bg-transparent px-3.5 py-2.5 text-left font-brand text-[13px] font-semibold text-white transition hover:bg-[#2f355b]";
</script>

<template>
    <div v-click-outside="close" class="relative" :class="open && 'z-30'">
        <!-- Panel sits behind the trigger, framed around it -->
        <div
            v-show="open"
            class="absolute -top-1.5 z-30 w-max min-w-[130px] overflow-hidden rounded-[4px] bg-brand-navy shadow-[2px_4px_20px_0px_rgba(0,0,0,0.25)]"
            :class="alignClasses[align]"
        >
            <!-- Offset-compensated invisible twin reserves the trigger's
                 exact footprint -->
            <div :class="twinPadClasses[align]">
                <span
                    aria-hidden="true"
                    class="invisible"
                    :class="triggerClass"
                >
                    <svg
                        viewBox="0 0 14 11"
                        :class="iconClass"
                        aria-hidden="true"
                    >
                        <rect width="8" height="1.5" />
                    </svg>
                    Move…
                </span>
            </div>
            <button type="button" :class="menuItemClasses" @click="moveToList">
                Move to List…
            </button>
            <button
                v-if="list.type !== 'expert'"
                type="button"
                :class="menuItemClasses"
                @click="moveToNewList"
            >
                Move to New List…
            </button>
        </div>

        <button
            type="button"
            class="relative disabled:pointer-events-none disabled:opacity-50"
            :class="[
                triggerClass,
                open && 'z-40 no-underline hover:!bg-transparent',
            ]"
            :style="open ? { color: '#fff' } : undefined"
            :disabled="disabled"
            @click="open = !open"
        >
            <svg
                viewBox="0 0 14 11"
                :class="iconClass"
                fill="currentColor"
                aria-hidden="true"
            >
                <path
                    d="M11.359 4.58333L10.6405 5.35417C10.4981 5.50695 10.4269 5.70139 10.4269 5.9375C10.4269 6.17361 10.4981 6.36806 10.6405 6.52083C10.7829 6.67361 10.9642 6.75 11.1842 6.75C11.4043 6.75 11.5856 6.67361 11.728 6.52083L13.767 4.33333C13.9223 4.16667 14 3.97222 14 3.75C14 3.52778 13.9223 3.33333 13.767 3.16667L11.728 0.979167C11.5856 0.826389 11.4043 0.75 11.1842 0.75C10.9642 0.75 10.7829 0.826389 10.6405 0.979167C10.4981 1.13194 10.4269 1.32639 10.4269 1.5625C10.4269 1.79861 10.4981 1.99306 10.6405 2.14583L11.359 2.91667H7.77677C7.55668 2.91667 7.37233 2.99667 7.22371 3.15667C7.07509 3.31667 7.00052 3.51444 7 3.75C6.99948 3.98556 7.07406 4.18361 7.22371 4.34417C7.37337 4.50472 7.55772 4.58444 7.77677 4.58333H11.359Z"
                />
                <rect y="3.05" width="5" height="1.5" rx="0.75" />
                <rect width="8" height="1.5" rx="0.75" />
                <rect y="6" width="8" height="1.5" rx="0.75" />
                <rect y="9" width="8" height="1.5" rx="0.75" />
            </svg>
            <!-- Open: icon only; the label keeps its footprint so the
                 trigger doesn't shift -->
            <span :class="open && 'invisible'">Move…</span>
        </button>
    </div>
</template>
